import {Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom, Observable} from "rxjs";
import {ModalController} from "@ionic/angular/standalone";
import {UserResponseModel} from "../api/models/user-response-model";
import {IdentityService} from "../api/services/identity.service";
import {Router} from "@angular/router";
import {InactivityService, InactivityLogoutReason} from "./inactivity.service";
import {PasskeyDeviceHintsService} from "./passkey-device-hints.service";

@Injectable({
  providedIn: 'root'
})
export class AuthStateService {
  private userSubject = new BehaviorSubject<UserResponseModel | null>(null);
  public user$: Observable<UserResponseModel | null> = this.userSubject.asObservable();
  private inactivityWatcherActive = false;

  constructor(
    private readonly identityService: IdentityService,
    private readonly router: Router,
    private readonly inactivityService: InactivityService,
    private readonly modalCtrl: ModalController,
    private readonly passkeyDeviceHints: PasskeyDeviceHintsService
  ) {
    this.inactivityService.registerLogoutCallback((reason: InactivityLogoutReason) => {
      const shouldBroadcast = reason !== 'storage';
      this.logOut({ broadcast: shouldBroadcast });
    });
  }

  setUser(user: UserResponseModel) {
    if (!user) {
      return;
    }

    this.userSubject.next(user);
    this.startInactivityWatcher();
  }

  getUser(): UserResponseModel | null {
    return this.userSubject.getValue();
  }

  async fetchAuthenticatedUser() {
    const user = await firstValueFrom(this.identityService.getAuthenticatedUser$Json());

    if (user) {
      this.setUser(user);
    }
  }


  clearUser() {
    this.stopInactivityWatcher();
    this.userSubject.next(null);
  }

  async dismissOpenModals(): Promise<void> {
    // Safety bound in case a modal refuses to dismiss.
    for (let i = 0; i < 10; i++) {
      const top = await this.modalCtrl.getTop();
      if (!top) return;
      try {
        await top.dismiss(null, 'session-ended');
      } catch {
        return;
      }
    }
  }

  logOut(options?: { broadcast?: boolean }) {
    const broadcast = options?.broadcast ?? true;

    void this.dismissOpenModals();
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    try {
      sessionStorage.clear();
    } catch {
    }
    // After the clear, so it survives into the login page: signing out must not
    // be undone by the automatic passkey prompt that waits there.
    this.passkeyDeviceHints.suppressNextAutoLogin();
    // The address is only kept to label a passkey for the signed-in account.
    this.passkeyDeviceHints.forgetAccountEmail();
    this.clearCookies();

    this.clearUser()

    if (broadcast) {
      localStorage.setItem('force-logout', Date.now().toString());
    }

    this.router.navigate(['login']);
  }

  private startInactivityWatcher(): void {
    if (this.inactivityWatcherActive) {
      return;
    }

    this.inactivityService.startWatching();
    this.inactivityWatcherActive = true;
  }

  private stopInactivityWatcher(): void {
    if (!this.inactivityWatcherActive) {
      return;
    }

    this.inactivityService.stopWatching();
    this.inactivityWatcherActive = false;
  }

  private clearCookies(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const cookies = document.cookie ? document.cookie.split(';') : [];

    cookies.forEach((cookie) => {
      const separatorIndex = cookie.indexOf('=');
      const cookieName = (separatorIndex > -1 ? cookie.slice(0, separatorIndex) : cookie).trim();

      if (!cookieName) {
        return;
      }

      document.cookie = `${cookieName}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    });
  }
}
